<template>
  <div class="container py-4">
    <!-- Compact Header -->
    <div class="mb-3">
      <!-- Title and Created date on same line -->
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
        <h2 class="mb-0">{{ headerTitle }}</h2>
        <div v-if="currentOption && !loadingData && materialized" class="text-muted small">
          Created {{ formatCompactDateTime(materialized.generated_at) }}
        </div>
      </div>

      <!-- Leaderboard, Location Selector, and Date Picker in one row -->
      <div class="d-flex flex-wrap align-items-center gap-3">
        <!-- Pam's Leaderboard (compact) -->
        <div class="flex-shrink-0">
          <LeaderboardWidget :auto-refresh="false" :compact="true" title="Pam's Leaderboard" />
        </div>

        <!-- Location Selector -->
        <div v-if="!loadingOptions && !noLocations && (options.length > 1 || (groupedParks && groupedParks.length > 0))" class="ms-auto">
          <label for="locationSelect" class="form-label small mb-1">Select Location:</label>
          <select id="locationSelect" class="form-select form-select-sm location-select" style="min-width: 260px" v-model.number="selectedLocationId" :disabled="loadingData">
            <!-- Grouped by Region & Season Status -->
            <template v-if="groupedParks && groupedParks.length > 0">
              <!-- OPEN PARKS - All regions first -->
              <template v-for="region in groupedParks" :key="`${region.region_id}-open`">
                <optgroup
                  :label="`${region.region_name} - Open (${region.open_count || 0})`"
                  v-if="region.open_parks && region.open_parks.length > 0"
                  class="optgroup-open">
                  <option v-for="park in region.open_parks" :key="park.id" :value="park.id" class="option-open">
                    {{ park.name }}
                  </option>
                </optgroup>
              </template>

              <!-- Divider (visual separator) -->
              <option disabled class="option-divider">────────────────────────</option>

              <!-- CLOSED PARKS - All regions after -->
              <template v-for="region in groupedParks" :key="`${region.region_id}-closed`">
                <optgroup
                  :label="`${region.region_name} - Closed (${region.closed_count || 0})`"
                  v-if="region.closed_parks && region.closed_parks.length > 0"
                  class="optgroup-closed">
                  <option v-for="park in region.closed_parks" :key="park.id" :value="park.id" class="option-closed">
                    {{ park.name }}
                  </option>
                </optgroup>
              </template>
            </template>

            <!-- Fallback to flat list if grouped data not available -->
            <template v-else>
              <option v-for="opt in options" :key="opt.location_id" :value="opt.location_id">
                {{ opt.label }}<span v-if="!opt.available_today"> (no data)</span>
              </option>
            </template>
          </select>
        </div>

        <!-- Date Picker -->
        <div v-if="!loadingOptions && selectedLocationId && availableDates.length > 0">
          <label for="dateSelect" class="form-label small mb-1">View Date:</label>
          <select id="dateSelect" class="form-select form-select-sm" style="min-width: 160px" v-model="selectedDate" :disabled="loadingData || loadingDates">
            <option v-for="date in availableDates" :key="date" :value="date">
              {{ formatDateOption(date) }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Options loading spinner -->
    <div v-if="loadingOptions" class="d-flex align-items-center mb-3">
      <div class="spinner-border text-secondary me-2" role="status" style="width: 1.5rem; height: 1.5rem;">
        <span class="visually-hidden">Loading...</span>
      </div>
      <span>Loading locations…</span>
    </div>

    <!-- No locations available -->
    <div v-if="!loadingOptions && noLocations" class="alert alert-warning" role="alert">
      No locations available.
    </div>

    <!-- Generic error -->
    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <!-- Data loading spinner -->
    <div v-if="loadingData" class="d-flex align-items-center mb-3">
      <div class="spinner-border text-secondary me-2" role="status" style="width: 1.5rem; height: 1.5rem;">
        <span class="visually-hidden">Loading...</span>
      </div>
      <span>Loading dashboard data…</span>
    </div>

    <!-- No data today for selected location -->
    <div v-if="!loadingData && noDataToday" class="alert alert-info" role="alert">
      No materialized data for today for this location.
    </div>

    <!-- Main content: layout modeled after HTML report -->
    <div v-if="!loadingData && materialized">

      <!-- KPI Summary Bar (matches email banner) -->
      <div class="kpi-summary-bar mb-4">
        <div class="kpi-summary-item">
          <div class="kpi-summary-label">Revenue (7d)</div>
          <div class="kpi-summary-value">
            <span v-if="br.last_7_days != null && br.last_7_days !== 0">{{ currency(br.last_7_days) }}</span>
            <span v-else class="text-muted">N/A</span>
          </div>
          <div class="kpi-summary-trend" :class="yoyClass(br.week_yoy_pct)" v-if="br.week_yoy_pct != null">
            {{ arrow(br.week_yoy_pct) }} {{ pctDisplay(br.week_yoy_pct) }}
          </div>
        </div>
        <div class="kpi-summary-divider"></div>
        <div class="kpi-summary-item">
          <div class="kpi-summary-label">Occupancy</div>
          <div class="kpi-summary-value">
            <span v-if="occ.current_rv_occupancy != null">{{ pct(occ.current_rv_occupancy) }}</span>
            <span v-else class="text-muted">N/A</span>
          </div>
          <div class="kpi-summary-meta">
            <span v-if="occ.current_rv_available !== undefined">
              {{ occ.current_rv_available || 0 }} RV available
            </span>
          </div>
        </div>
        <div class="kpi-summary-divider"></div>
        <div class="kpi-summary-item">
          <div class="kpi-summary-label">Calls (7d)</div>
          <div class="kpi-summary-value">
            <span v-if="call.last_7_days?.answer_rate != null">{{ pct(call.last_7_days.answer_rate) }}</span>
            <span v-else class="text-muted">N/A</span>
          </div>
          <div class="kpi-summary-status" :class="call.last_7_days?.answer_rate >= 90 ? 'status-good' : 'status-warning'" v-if="call.last_7_days?.answer_rate != null">
            <span v-if="call.last_7_days.answer_rate >= 90">✓ Good</span>
            <span v-else>⚠ Below 90%</span>
          </div>
        </div>
      </div>

      <!-- Weather Forecast Widget (Compact) -->
      <div v-if="weather.current_temp" class="mb-3">
        <div class="card border">
          <div class="card-header text-uppercase small fw-semibold py-2">🌤️ Weather Forecast</div>
          <div class="card-body py-2 px-3">
            <!-- Weather Alerts -->
            <div v-if="weather.today_alert" class="alert alert-warning mb-2 py-1 px-2 small">
              <i class="bi bi-exclamation-triangle-fill"></i> <strong>Alert:</strong> {{ weather.today_alert }}
            </div>

            <div class="row g-2 align-items-center">
              <!-- Current Conditions (Compact) -->
              <div class="col-md-3">
                <div class="text-center">
                  <div class="display-6 fw-bold mb-1">{{ weather.current_temp }}°F</div>
                  <div class="small text-muted">{{ weather.today_high }}° / {{ weather.today_low }}°</div>
                </div>
              </div>

              <!-- 7-Day Forecast (Compact with Icons) -->
              <div class="col-md-9">
                <div class="d-flex justify-content-between">
                  <div v-for="(day, idx) in weather.forecast_days?.slice(0, 7)" :key="idx" class="text-center flex-fill" style="min-width: 0;">
                    <div class="x-small text-truncate">{{ formatForecastDate(day.date) }}</div>
                    <div class="fs-5">{{ getWeatherEmoji(day.weathercode) }}</div>
                    <div class="small fw-bold">{{ day.high }}°</div>
                    <div class="x-small text-muted">{{ day.low }}°</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="small text-muted mt-1 text-end">
              Last updated: {{ weather.last_update }}
            </div>
          </div>
        </div>
      </div>

      <!-- Key Performance Metrics -->
      <h4 class="mt-4 mb-3">Key Performance Metrics</h4>
      <div class="row g-3 align-items-stretch">
        <!-- Booking Revenue Section -->
        <div class="col-12" id="revenue">
          <div class="card h-100 border">
            <div class="card-header text-uppercase small fw-semibold">💰 Booking Revenue</div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="text-muted small">Last 7 Days</div>
                  <div class="fw-semibold">{{ currency(br.last_7_days) }}</div>
                  <div class="small">
                    vs {{ currency(br.same_7_days_last_year) }} LY
                    <span :class="yoyClass(br.week_yoy_pct)">{{ arrow(br.week_yoy_pct) }} {{ pctDisplay(br.week_yoy_pct) }}</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="text-muted small">Yesterday</div>
                  <div class="fw-semibold">{{ currency(br.yesterday) }}</div>
                  <div class="small">
                    vs {{ currency(br.last_year_same_day) }} LY
                    <span :class="yoyClass(br.day_yoy_pct)">{{ arrow(br.day_yoy_pct) }} {{ pctDisplay(br.day_yoy_pct) }}</span>
                  </div>
                </div>
              </div>

              <!-- AI Insights (collapsible, starts expanded) -->
              <div v-if="aiSection('revenue_performance')" class="mt-3 pt-3 border-top">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div class="fw-semibold text-primary">
                    <i class="bi bi-lightbulb-fill"></i> PAM's Insights
                  </div>
                  <button
                    @click="toggleAISection('revenue')"
                    class="btn btn-sm btn-link text-decoration-none p-0"
                    type="button"
                  >
                    <i :class="aiExpanded.revenue ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </button>
                </div>
                <div v-show="aiExpanded.revenue" class="small text-muted">
                  {{ aiSection('revenue_performance') }}
                </div>
              </div>
            </div>
            <FeedbackButtons
              v-if="selectedLocationId"
              section="booking_revenue"
              :section-id="SECTION_IDS.REVENUE_PERFORMANCE"
              :location-id="selectedLocationId"
              :materialization-id="materialized?.id"
              @feedback-submitted="engagement.trackEvent('feedback_given', { section: 'booking_revenue' })"
            />
          </div>
        </div>

        <!-- Occupancy Section -->
        <div class="col-12" id="occupancy">
          <div class="card h-100 border">
            <div class="card-header text-uppercase small fw-semibold">📊 Occupancy & Outlook</div>
            <div class="card-body">
              <div class="row g-3 mb-3">
                <div class="col-md-4">
                  <div class="text-muted small">Today</div>
                  <div><b>RV:</b> {{ pct(occ.current_rv_occupancy) }}</div>
                  <div v-if="occ.current_house_occupancy != null"><b>Lodging:</b> {{ pct(occ.current_house_occupancy) }}</div>
                  <div v-if="occ.current_parking_occupancy != null"><b>Parking:</b> {{ pct(occ.current_parking_occupancy) }}</div>
                  <div v-if="occ.current_tent_occupancy != null"><b>Tent:</b> {{ pct(occ.current_tent_occupancy) }}</div>
                  <div v-if="occ.current_golf_cart_occupancy != null"><b>Golf Carts:</b> {{ pct(occ.current_golf_cart_occupancy) }}</div>
                </div>
                <div class="col-md-4">
                  <div class="text-muted small">Yesterday</div>
                  <div><b>RV:</b> {{ pct(occ.yesterday_rv_occupancy) }}</div>
                  <div v-if="occ.yesterday_house_occupancy != null"><b>Lodging:</b> {{ pct(occ.yesterday_house_occupancy) }}</div>
                  <div v-if="occ.yesterday_parking_occupancy != null"><b>Parking:</b> {{ pct(occ.yesterday_parking_occupancy) }}</div>
                  <div v-if="occ.yesterday_tent_occupancy != null"><b>Tent:</b> {{ pct(occ.yesterday_tent_occupancy) }}</div>
                  <div v-if="occ.yesterday_golf_cart_occupancy != null"><b>Golf Carts:</b> {{ pct(occ.yesterday_golf_cart_occupancy) }}</div>
                </div>
                <div class="col-md-4">
                  <div class="text-muted small">Next 7 Days Avg</div>
                  <div><b>RV:</b> {{ pct(occ.week1_2_avg_occupancy) }}</div>
                  <div v-if="occ.week1_2_house_occupancy != null"><b>Lodging:</b> {{ pct(occ.week1_2_house_occupancy) }}</div>
                  <div v-if="occ.week1_2_parking_occupancy != null"><b>Parking:</b> {{ pct(occ.week1_2_parking_occupancy) }}</div>
                  <div v-if="occ.week1_2_tent_occupancy != null"><b>Tent:</b> {{ pct(occ.week1_2_tent_occupancy) }}</div>
                  <div v-if="occ.week1_2_golf_cart_occupancy != null"><b>Golf Carts:</b> {{ pct(occ.week1_2_golf_cart_occupancy) }}</div>
                </div>
              </div>

              <!-- AI Insights (collapsible, starts expanded) -->
              <div v-if="aiSection('occupancy_outlook')" class="mt-3 pt-3 border-top">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div class="fw-semibold text-primary">
                    <i class="bi bi-lightbulb-fill"></i> PAM's Insights
                  </div>
                  <button
                    @click="toggleAISection('occupancy')"
                    class="btn btn-sm btn-link text-decoration-none p-0"
                    type="button"
                  >
                    <i :class="aiExpanded.occupancy ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </button>
                </div>
                <div v-show="aiExpanded.occupancy" class="small text-muted">
                  {{ aiSection('occupancy_outlook') }}
                </div>
              </div>
            </div>
            <FeedbackButtons
              v-if="selectedLocationId"
              section="occupancy"
              :section-id="SECTION_IDS.OCCUPANCY_OUTLOOK"
              :location-id="selectedLocationId"
              :materialization-id="materialized?.id"
              @feedback-submitted="engagement.trackEvent('feedback_given', { section: 'occupancy' })"
            />
          </div>
        </div>

        <!-- Guest Flow Section -->
        <div class="col-12" id="guests">
          <div class="card h-100 border">
            <div class="card-header text-uppercase small fw-semibold">👥 Guest Flow</div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="text-muted small mb-2">Today</div>
                  <div class="mb-2">
                    <span class="text-success">↓</span> <strong>{{ arrivals.total ?? 0 }}</strong> Arrivals
                    <span v-if="arrivals.lodging_count" class="badge bg-warning text-dark ms-2">{{ arrivals.lodging_count }} Lodging (Spot Check)</span>
                  </div>
                  <div>
                    <span class="text-primary">↑</span> <strong>{{ departures.total ?? 0 }}</strong> Departures
                    <span v-if="departures.lodging_count" class="badge bg-danger ms-2">{{ departures.lodging_count }} Lodging (Full Clean)</span>
                    <span v-if="departures.vip_count" class="badge bg-warning text-dark ms-1">{{ departures.vip_count }} VIP</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="text-muted small mb-2">Tomorrow</div>
                  <div class="mb-2">
                    <span class="text-success">↓</span> <strong>{{ arrivals.tomorrow_total ?? 0 }}</strong> Arrivals
                    <span v-if="arrivals.tomorrow_lodging_count" class="badge bg-warning text-dark ms-2">{{ arrivals.tomorrow_lodging_count }} Lodging (Spot Check)</span>
                  </div>
                  <div>
                    <span class="text-primary">↑</span> <strong>{{ departures.tomorrow_total ?? 0 }}</strong> Departures
                    <span v-if="departures.tomorrow_lodging_count" class="badge bg-danger ms-2">{{ departures.tomorrow_lodging_count }} Lodging (Full Clean)</span>
                    <span v-if="departures.tomorrow_vip_count" class="badge bg-warning text-dark ms-1">{{ departures.tomorrow_vip_count }} VIP</span>
                  </div>
                </div>
              </div>

              <!-- Guest Loyalty Focus -->
              <div v-if="guestFlowDetails.length > 0" class="mt-3 pt-3 border-top">
                <div class="alert alert-info mb-2 py-2">
                  <i class="bi bi-info-circle-fill"></i> <strong>Build Guest Loyalty:</strong> New guests (1 stay) are your future! VIPs (3+ stays) drive growth.
                </div>

                <!-- Departures with stay counts -->
                <div v-if="departureGuests.length > 0">
                  <div class="small fw-bold mb-2">👋 Departing Today (Build Connection!):</div>
                  <div class="row g-2 mb-3">
                    <div v-for="guest in departureGuests" :key="'dep-' + guest.index" class="col-12">
                      <div class="card border" :class="guest.stayClass">
                        <div class="card-body py-2">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{{ guest.name || 'Guest' }}</strong>
                              <span v-if="guest.unit" class="text-muted ms-1">- Unit {{ guest.unit }}</span>
                            </div>
                            <div>
                              <span class="badge" :class="guest.badgeClass">
                                {{ guest.stayLabel }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Arrivals with stay counts -->
                <div v-if="arrivalGuests.length > 0">
                  <div class="small fw-bold mb-2">🎉 Arriving Today (Welcome!):</div>
                  <div class="row g-2">
                    <div v-for="guest in arrivalGuests" :key="'arr-' + guest.index" class="col-12">
                      <div class="card border" :class="guest.stayClass">
                        <div class="card-body py-2">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{{ guest.name || 'Guest' }}</strong>
                              <span v-if="guest.unit" class="text-muted ms-1">- Unit {{ guest.unit }}</span>
                            </div>
                            <div>
                              <span class="badge" :class="guest.badgeClass">
                                {{ guest.stayLabel }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AI Insights (collapsible, starts expanded) -->
              <div v-if="aiSection('guest_flow')" class="mt-3 pt-3 border-top">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div class="fw-semibold text-primary">
                    <i class="bi bi-lightbulb-fill"></i> PAM's Insights
                  </div>
                  <button
                    @click="toggleAISection('guest_flow')"
                    class="btn btn-sm btn-link text-decoration-none p-0"
                    type="button"
                  >
                    <i :class="aiExpanded.guest_flow ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </button>
                </div>
                <div v-show="aiExpanded.guest_flow" class="small text-muted">
                  {{ aiSection('guest_flow') }}
                </div>
              </div>
            </div>
            <FeedbackButtons
              v-if="selectedLocationId"
              section="guest_flow"
              :section-id="SECTION_IDS.GUEST_FLOW"
              :location-id="selectedLocationId"
              :materialization-id="materialized?.id"
              @feedback-submitted="engagement.trackEvent('feedback_given', { section: 'guest_flow' })"
            />
          </div>
        </div>

        <!-- Call Center Section -->
        <div class="col-12" id="calls">
          <div class="card h-100 border">
            <div class="card-header text-uppercase small fw-semibold">📞 Call Center</div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="text-muted small">Last 7 Days</div>
                  <div class="fw-semibold">{{ pct(call.last_7_days?.answer_rate ?? 0) }}</div>
                  <div class="small">{{ call.last_7_days?.answered_calls ?? 0 }}/{{ call.last_7_days?.total_calls ?? 0 }} calls</div>
                </div>
                <div class="col-md-6">
                  <div class="text-muted small">Yesterday</div>
                  <div class="fw-semibold">{{ pct(call.yesterday?.answer_rate ?? 0) }}</div>
                  <div class="small">{{ call.yesterday?.answered_calls ?? 0 }}/{{ call.yesterday?.total_calls ?? 0 }} calls</div>
                </div>
              </div>

              <!-- AI Insights (collapsible, starts expanded) -->
              <div v-if="aiSection('operational_health')" class="mt-3 pt-3 border-top">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div class="fw-semibold text-primary">
                    <i class="bi bi-lightbulb-fill"></i> PAM's Insights
                  </div>
                  <button
                    @click="toggleAISection('call_center')"
                    class="btn btn-sm btn-link text-decoration-none p-0"
                    type="button"
                  >
                    <i :class="aiExpanded.call_center ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </button>
                </div>
                <div v-show="aiExpanded.call_center" class="small text-muted">
                  {{ aiSection('operational_health') }}
                </div>
              </div>
            </div>
            <FeedbackButtons
              v-if="selectedLocationId"
              section="call_center"
              :section-id="SECTION_IDS.OPERATIONAL_HEALTH"
              :location-id="selectedLocationId"
              :materialization-id="materialized?.id"
              @feedback-submitted="engagement.trackEvent('feedback_given', { section: 'call_center' })"
            />
          </div>
        </div>

        <!-- Social Reputation Section -->
        <div class="col-12" id="social" v-if="socialReputation.hasData">
          <div class="card h-100 border">
            <div class="card-header text-uppercase small fw-semibold">⭐ Social Reputation</div>
            <div class="card-body">
              <!-- Recent Reviews -->
              <div v-if="socialReputation.reviews && socialReputation.reviews.length > 0">
                <div class="fw-semibold mb-3">
                  {{ socialReputation.reviews.length }} review(s) received (last 30 days)
                </div>

                <!-- Show most recent 3 reviews -->
                <div v-for="(review, idx) in socialReputation.reviews.slice(0, 3)" :key="idx"
                     class="mb-2 p-2 rounded border"
                     :style="{
                       borderLeft: `3px solid ${review.sentiment === 'negative' ? '#ef4444' : review.sentiment === 'neutral' ? '#f59e0b' : '#22c55e'}`
                     }">
                  <div class="small">
                    <span style="color: #f59e0b;">{{ review.rating_stars }}</span>
                    <span class="text-muted ms-1">{{ review.network }}</span>
                    <span class="ms-2" :style="{color: review.sentiment === 'negative' ? '#ef4444' : review.sentiment === 'neutral' ? '#f59e0b' : '#22c55e', fontWeight: 'bold'}">
                      {{ review.sentiment === 'negative' ? '🔴 NEGATIVE' : review.sentiment === 'neutral' ? '🟡 NEUTRAL' : '🟢 POSITIVE' }}
                    </span>
                  </div>
                  <div class="fw-semibold small">
                    {{ review.author }}
                    <span v-if="review.matched_guest && review.match_confidence > 0.8" class="text-primary ms-1">
                      • GUEST: {{ review.matched_guest }} (Site {{ review.guest_site }})
                    </span>
                  </div>
                  <div v-if="review.text" class="small fst-italic text-muted mt-1">
                    "{{ review.text.substring(0, 150) }}{{ review.text.length > 150 ? '...' : '' }}"
                  </div>
                  <div v-if="review.needs_response" class="small text-danger fw-bold mt-1">
                    ⚠️ NEEDS RESPONSE
                  </div>
                </div>

                <div v-if="socialReputation.reviews.length > 3" class="small text-muted">
                  + {{ socialReputation.reviews.length - 3 }} more review(s)
                </div>
              </div>

              <div v-else class="text-muted small">
                No reviews received in the last 30 days
              </div>

              <!-- Social Metrics -->
              <div v-if="socialReputation.metrics" class="mt-3 pt-3 border-top">
                <div class="small fw-semibold mb-2">Platform Performance (Last 30 Days)</div>
                <div v-if="socialReputation.metrics.review_summary" class="small mb-2">
                  • Overall Rating: <strong>{{ socialReputation.metrics.review_summary.average_rating }}</strong>/5.0
                  • <strong>{{ socialReputation.metrics.review_summary.open_for_response }}</strong> awaiting response
                </div>
                <div v-if="socialReputation.metrics.social_metrics" class="small">
                  <div v-if="socialReputation.metrics.social_metrics.facebook">
                    • <strong>Facebook:</strong> {{ formatNumber(socialReputation.metrics.social_metrics.facebook.likes) }} likes, {{ formatNumber(socialReputation.metrics.social_metrics.facebook.reach) }} reach
                  </div>
                  <div v-if="socialReputation.metrics.social_metrics.instagram">
                    • <strong>Instagram:</strong> {{ formatNumber(socialReputation.metrics.social_metrics.instagram.followers) }} followers
                  </div>
                </div>

                <!-- Social Media Links -->
                <div v-if="socialReputation.socialUrls && (socialReputation.socialUrls.facebook || socialReputation.socialUrls.instagram || socialReputation.socialUrls.google)"
                     class="small text-muted mt-2 pt-2 border-top">
                  📱 <strong>View on:</strong>
                  <a v-if="socialReputation.socialUrls.facebook" :href="socialReputation.socialUrls.facebook" target="_blank" class="text-decoration-none ms-1">Facebook</a>
                  <span v-if="socialReputation.socialUrls.facebook && (socialReputation.socialUrls.instagram || socialReputation.socialUrls.google)"> • </span>
                  <a v-if="socialReputation.socialUrls.instagram" :href="socialReputation.socialUrls.instagram" target="_blank" class="text-decoration-none">Instagram</a>
                  <span v-if="socialReputation.socialUrls.instagram && socialReputation.socialUrls.google"> • </span>
                  <a v-if="socialReputation.socialUrls.google" :href="socialReputation.socialUrls.google" target="_blank" class="text-decoration-none">Google</a>
                </div>
              </div>

              <!-- AI Insights (collapsible, starts expanded) -->
              <div v-if="aiSection('guest_experience')" class="mt-3 pt-3 border-top">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div class="fw-semibold text-primary">
                    <i class="bi bi-lightbulb-fill"></i> PAM's Insights
                  </div>
                  <button
                    @click="toggleAISection('social')"
                    class="btn btn-sm btn-link text-decoration-none p-0"
                    type="button"
                  >
                    <i :class="aiExpanded.social ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </button>
                </div>
                <div v-show="aiExpanded.social" class="small text-muted">
                  {{ aiSection('guest_experience') }}
                </div>
              </div>
            </div>

            <FeedbackButtons
              v-if="selectedLocationId"
              section="social_reputation"
              :section-id="SECTION_IDS.GUEST_EXPERIENCE"
              :location-id="selectedLocationId"
              :materialization-id="materialized?.id"
              @feedback-submitted="engagement.trackEvent('feedback_given', { section: 'social_reputation' })"
            />
          </div>
        </div>
      </div>

      <!-- Performance Analysis -->
      <!-- Today's Action Plan -->
      <h4 class="mt-4 mb-3">Today's Action Plan</h4>
      <div class="row g-3">
        <div class="col-12" v-for="(task, idx) in actionPlan" :key="idx">
          <div class="card card-left-accent border-dark">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="fw-semibold">{{ task.title || 'Action Item' }}</div>
                <span v-if="task.badge" class="badge bg-secondary ms-2">{{ task.badge }}</span>
              </div>
              <div class="text-muted">{{ task.subtitle }}</div>
            </div>
          </div>
        </div>
        <div class="col-12" v-if="!actionPlan.length">
          <div class="text-muted">No action items for today.</div>
        </div>
      </div>

      <!-- Strategic Recommendations -->
      <h4 class="mt-4 mb-3">Strategic Recommendations</h4>
      <div class="row g-3">
        <div class="col-12" v-for="(rec, idx) in recommendations" :key="idx">
          <div class="card border-primary">
            <div class="card-body">
              <div class="fw-semibold mb-1">{{ rec.title || 'Recommendation' }}</div>
              <div class="mb-1">{{ rec.subtitle }}</div>
              <div class="fst-italic" v-if="rec.detail">{{ rec.detail }}</div>
            </div>
          </div>
        </div>
        <div class="col-12" v-if="!recommendations.length">
          <div class="text-muted">No recommendations.</div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { getMaterializedOptions } from '@/services/dashboard';
import FeedbackButtons from '@/components/FeedbackButtons.vue';
import LeaderboardWidget from '@/components/LeaderboardWidget.vue';
import { useEngagementTracking } from '@/composables/useEngagementTracking';
import http from '@/api/http';
import activityTracker from '@/services/activityTracker';

export default {
  name: 'DashboardView',
  components: {
    FeedbackButtons,
    LeaderboardWidget
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useAuthStore();

    // Section IDs for feedback (matching database sections table)
    const SECTION_IDS = {
      SUMMARY: 10,
      REVENUE_PERFORMANCE: 1,
      OCCUPANCY_OUTLOOK: 2,
      GUEST_FLOW: 3,
      OPERATIONAL_HEALTH: 4,
      GUEST_EXPERIENCE: 5,
      REVENUE_INSIGHTS: 6,
      OCCUPANCY_INSIGHTS: 7,
      OPERATIONS_INSIGHTS: 8,
      GUEST_INSIGHTS: 9
    };
    store.syncFromStorage();

    const loadingOptions = ref(true);
    const loadingData = ref(false);
    const loadingDates = ref(false);
    const errorMessage = ref('');

    const options = ref([]); // [{ label, location_id, available_today }]
    const groupedParks = ref([]); // Grouped by region & season status
    const noLocations = ref(false);

    const selectedLocationId = ref(null);
    const availableDates = ref([]); // Array of date strings
    const selectedDate = ref(null); // Currently selected date

    const materialized = ref(null);
    const noDataToday = ref(false);

    // Initialize engagement tracking
    const engagement = useEngagementTracking(selectedLocationId, computed(() => materialized.value?.id || null));

    const currentOption = computed(() => {
      if (!options.value?.length || selectedLocationId.value == null) return null;
      return options.value.find(o => o.location_id === selectedLocationId.value) || null;
    });

    const prettyJson = (obj) => {
      try { return JSON.stringify(obj ?? {}, null, 2); } catch { return String(obj ?? ''); }
    };

    // Formatting helpers
    const currency = (n) => {
      const x = Number(n || 0);
      return x.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
    };
    const pctDisplay = (p) => {
      const v = Number(p ?? 0);
      return `${Math.abs(v).toFixed(1)}%`;
    };
    const pct = (v) => `${Number(v || 0).toFixed(1)}%`;
    const arrow = (p) => Number(p ?? 0) >= 0 ? '↑' : '↓';
    const yoyClass = (p) => Number(p ?? 0) >= 0 ? 'text-success' : 'text-danger';

    // Read metrics from pre-computed metrics_json (single source of truth)
    const br = computed(() => {
      const metrics = materialized.value?.metrics_json?.booking_revenue || {};
      return {
        yesterday: metrics.yesterday || 0,
        last_year_same_day: metrics.last_year_same_day || 0,
        last_7_days: metrics.last_7_days || 0,
        same_7_days_last_year: metrics.same_7_days_last_year || 0,
        last_30_days: metrics.last_30_days || 0,
        same_30_days_last_year: metrics.same_30_days_last_year || 0,
        day_yoy_pct: metrics.daily_yoy_change_pct || 0,
        week_yoy_pct: metrics.weekly_yoy_change_pct || 0,
        month_yoy_pct: metrics.monthly_yoy_change_pct || 0,
      };
    });

    const occ = computed(() => {
      const metrics = materialized.value?.metrics_json?.occupancy || {};
      return {
        current_rv_occupancy: metrics.current_rv_occupancy || 0,
        current_rv_available: metrics.current_rv_available || 0,
        yesterday_rv_occupancy: metrics.yesterday_rv_occupancy || 0,
        week1_2_avg_occupancy: metrics.week1_2_avg_occupancy || 0,
        has_data: metrics.has_data || false
      };
    });

    const arrivals = computed(() => {
      const metrics = materialized.value?.metrics_json?.arrivals || {};
      return {
        total: metrics.total || 0,
        vip_count: metrics.vip_count || 0
      };
    });

    const departures = computed(() => {
      const metrics = materialized.value?.metrics_json?.departures || {};
      return {
        total: metrics.total || 0,
        vip_count: metrics.vip_count || 0
      };
    });

    const call = computed(() => {
      const metrics = materialized.value?.metrics_json?.call_stats || {};
      return {
        last_7_days: metrics.last_7_days || {
          total_calls: 0,
          answered_calls: 0,
          missed_calls: 0,
          answer_rate: 0
        },
        yesterday: metrics.yesterday || {
          total_calls: 0,
          answered_calls: 0,
          missed_calls: 0,
          answer_rate: 0
        }
      };
    });

    // Weather data from WordPress
    const weather = computed(() => materialized.value?.payload_json?.queries?.weather_current_forecast || {});

    // Guest flow details with loyalty focus
    const processGuestList = (guestList) => {
      if (!Array.isArray(guestList) || guestList.length === 0) return [];
      return guestList.map((g, idx) => {
        const stays = g.StaysCount || g.stays_count || g.stays || 0;
        let stayLabel, badgeClass, stayClass;
        if (stays === 1) {
          stayLabel = '🌟 First Stay';
          badgeClass = 'bg-success';
          stayClass = 'border-success';
        } else if (stays === 2) {
          stayLabel = '👍 2nd Stay';
          badgeClass = 'bg-info';
          stayClass = 'border-info';
        } else if (stays >= 3) {
          stayLabel = `⭐ VIP (${stays} stays)`;
          badgeClass = 'bg-warning text-dark';
          stayClass = 'border-warning';
        } else {
          stayLabel = 'Guest';
          badgeClass = 'bg-secondary';
          stayClass = '';
        }

        // Construct guest name from FirstName + LastName or use GuestName
        let guestName = g.GuestName || g.guest_name;
        if (!guestName && (g.FirstName || g.LastName)) {
          const first = (g.FirstName || '').trim();
          const last = (g.LastName || '').trim();
          guestName = `${first} ${last}`.trim() || null;
        }

        // Get unit name/number
        const unit = g.Unit_Name || g.UnitName || g.unit_name || g.UnitNumber || g.unit || null;

        return {
          index: idx,
          name: guestName,
          unit: unit,
          stays,
          stayLabel,
          badgeClass,
          stayClass
        };
      });
    };

    const arrivalGuests = computed(() => {
      const rawArrivals = materialized.value?.payload_json?.queries?.arrivals || [];
      return processGuestList(rawArrivals);
    });

    const departureGuests = computed(() => {
      const rawDepartures = materialized.value?.payload_json?.queries?.departures || [];
      return processGuestList(rawDepartures);
    });

    const guestFlowDetails = computed(() => {
      return [...arrivalGuests.value, ...departureGuests.value];
    });

    // Social Reputation (from queries, not metrics)
    const socialReputation = computed(() => {
      // Check if we have soci_metrics in queries (social media data)
      const sociMetrics = materialized.value?.payload_json?.queries?.soci_metrics || null;
      const sociReviews = materialized.value?.payload_json?.queries?.soci_reviews || [];

      return {
        hasData: sociMetrics !== null || sociReviews.length > 0,
        reviews: sociReviews,
        metrics: sociMetrics,
        socialUrls: sociMetrics?.social_urls || {}
      };
    });

    const formatNumber = (val) => {
      if (val == null || val === 'N/A') return 'N/A';
      const num = Number(val);
      if (isNaN(num)) return val;
      return num.toLocaleString();
    };

    const formatGeneratedTime = (timestamp) => {
      if (!timestamp) return '';
      // Convert UTC timestamp to user's timezone (default: Pacific Time)
      // TODO: Get user timezone from user preferences/profile
      const userTimezone = 'America/Los_Angeles'; // Default to PT for RJourney HQ
      const timezoneAbbr = 'PT'; // Display abbreviation

      // Ensure timestamp is treated as UTC by appending 'Z' if not present
      let utcTimestamp = timestamp;
      if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('UTC')) {
        utcTimestamp = timestamp + 'Z';
      }

      const date = new Date(utcTimestamp);
      const options = {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      };
      const localString = date.toLocaleString('en-US', options);
      return localString + ' ' + timezoneAbbr;
    };

    const formatForecastDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr + 'T00:00:00'); // Treat as local date
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Check if it's today or tomorrow
      if (date.toDateString() === today.toDateString()) return 'Today';
      if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

      // Otherwise return day of week
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    };

    // Get weather emoji based on WMO weather code
    const getWeatherEmoji = (weathercode) => {
      const code = parseInt(weathercode);
      if (isNaN(code)) return '🌤️';

      // WMO Weather interpretation codes
      // https://open-meteo.com/en/docs
      if (code === 0) return '☀️';  // Clear sky
      if (code === 1) return '🌤️';  // Mainly clear
      if (code === 2) return '⛅';  // Partly cloudy
      if (code === 3) return '☁️';  // Overcast
      if (code === 45 || code === 48) return '🌫️';  // Fog
      if (code === 51 || code === 53 || code === 55) return '🌦️';  // Drizzle
      if (code === 61 || code === 63 || code === 65) return '🌧️';  // Rain
      if (code === 71 || code === 73 || code === 75) return '🌨️';  // Snow
      if (code === 77) return '❄️';  // Snow grains
      if (code === 80 || code === 81 || code === 82) return '🌧️';  // Rain showers
      if (code === 85 || code === 86) return '🌨️';  // Snow showers
      if (code === 95) return '⛈️';  // Thunderstorm
      if (code === 96 || code === 99) return '⛈️';  // Thunderstorm with hail

      return '🌤️';  // Default
    };

    const callAccentClass = computed(() => {
      const s = (call.value?.severity || '').toLowerCase();
      if (s === 'warning') return 'border-warning';
      if (s === 'critical' || s === 'danger') return 'border-danger';
      if (s === 'normal') return 'border-success';
      return 'border-secondary';
    });

    const headerTitle = computed(() => {
      const name = materialized.value?.payload_json?.meta?.park_name || currentOption.value?.label || 'Dashboard';
      // Remove suffix like ", LLC" or " RV Resort" for cleaner display
      const cleanName = name.replace(/,?\s*(LLC|RV Resort|Resort)$/i, '');
      return `${cleanName} - Daily Report`;
    });

    // Format compact date/time: "MM/DD HH:MM PT"
    const formatCompactDateTime = (timestamp) => {
      if (!timestamp) return '';
      const date = new Date(timestamp + (timestamp.includes('Z') ? '' : 'Z'));
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      // Determine timezone abbreviation (PT/PST/PDT based on date)
      const options = { timeZone: 'America/Los_Angeles', timeZoneName: 'short' };
      const tzPart = date.toLocaleString('en-US', options).split(' ').pop();

      return `${month}/${day} ${hours}:${minutes} ${tzPart}`;
    };

    // AI helpers
    const ai = computed(() => materialized.value?.ai_json || {});
    const aiSection = (key) => {
      // Map section keys to AI JSON structure
      // Insights are stored in both insights.{key} AND performance_snapshot.{analysis_key}
      const keyMap = {
        'revenue_performance': 'insights.revenue_performance',
        'guest_experience': 'insights.guest_experience',
        'operational_health': 'insights.operational_health',
        'occupancy_outlook': 'insights.occupancy_outlook',
        'guest_flow': 'performance_snapshot.los_analysis'
      };

      const mappedKey = keyMap[key] || key;
      const keys = mappedKey.split('.');
      let v = ai.value;

      for (const k of keys) {
        v = v?.[k];
        if (v === undefined) break;
      }

      if (!v) return '';
      if (typeof v === 'string') return v;
      // Handle wrapped responses like {"response": "..."} from AI errors
      if (typeof v === 'object' && v.response && typeof v.response === 'string') {
        return v.response;
      }
      try { return JSON.stringify(v); } catch { return String(v); }
    };
    const actionPlan = computed(() => {
      // Handle action_items.critical structure
      const a = ai.value?.action_items?.critical || ai.value?.action_plan || ai.value?.actions || [];
      if (Array.isArray(a)) return a.map(x => ({
        title: x.title || x.name || x.task || 'Action',
        badge: x.timeline || x.when || x.due || x.badge || '',
        subtitle: x.description || x.subtitle || x.detail || ''
      }));
      return [];
    });
    const recommendations = computed(() => {
      // Handle recommendations object with pricing/retention/operations keys
      const recs = ai.value?.recommendations || {};
      if (Array.isArray(recs)) {
        // Legacy array format
        return recs.map(x => ({
          title: x.title || x.name || 'Recommendation',
          subtitle: x.subtitle || x.summary || x.recommendation || '',
          detail: x.detail || x.description || x.rationale || ''
        }));
      } else if (typeof recs === 'object') {
        // New object format with pricing/retention/operations
        const result = [];
        if (recs.pricing) {
          result.push({
            title: '💰 Pricing Strategy',
            subtitle: recs.pricing.recommendation || '',
            detail: recs.pricing.rationale || ''
          });
        }
        if (recs.retention) {
          result.push({
            title: '🌟 Guest Experience',
            subtitle: recs.retention.recommendation || '',
            detail: recs.retention.rationale || ''
          });
        }
        if (recs.operations) {
          result.push({
            title: '⚙️ Operations',
            subtitle: recs.operations.recommendation || '',
            detail: recs.operations.rationale || ''
          });
        }
        return result;
      }
      return [];
    });

    const feedbackUrl = computed(() => {
      if (!materialized.value?.session_id) return '';
      const apiBase = process.env.VUE_APP_API_BASE_URL || '';
      return `${apiBase}/feedback/${materialized.value.session_id}`;
    });

    // PAM's Insights expansion state (all start expanded)
    const aiExpanded = ref({
      revenue: true,
      occupancy: true,
      call_center: true,
      social: true
    });

    const toggleAISection = (section) => {
      aiExpanded.value[section] = !aiExpanded.value[section];
    };

    async function loadOptions() {
      loadingOptions.value = true;
      errorMessage.value = '';
      noLocations.value = false;
      try {
        // Load grouped parks for dropdown (only if not already loaded - cache for session)
        if (groupedParks.value.length === 0) {
          try {
            const groupedRes = await http.get('/api/v1/locations/parks/grouped');
            groupedParks.value = groupedRes.data.regions || [];
          } catch (e) {
            console.warn('Failed to load grouped parks, will use flat list', e);
            groupedParks.value = [];
          }
        }

        const res = await getMaterializedOptions();
        const data = res?.data || {};
        const items = Array.isArray(data.options) ? data.options : [];
        options.value = items;
        if (!items.length) {
          noLocations.value = true;
          selectedLocationId.value = null;
          materialized.value = null;
        } else {
          // Check for location query parameter from magic link (try both location_id and location)
          const queryLocationId = route.query.location_id
            ? parseInt(route.query.location_id, 10)
            : (route.query.location ? parseInt(route.query.location, 10) : null);

          let chosen;
          if (queryLocationId && items.some(i => i.location_id === queryLocationId)) {
            // Use location from query parameter if valid
            chosen = queryLocationId;
            // Clean up query parameter after using it
            router.replace({ name: 'dashboard', query: {} });
          } else {
            // Fall back to default or first item
            const defId = data.default_location_id;
            chosen = (typeof defId === 'number' && items.some(i => i.location_id === defId))
              ? defId
              : items[0].location_id;
          }

          selectedLocationId.value = chosen;
          // Note: loadMaterialized() will be called by the watcher chain:
          // selectedLocationId watcher → loadAvailableDates() → sets selectedDate → selectedDate watcher → loadMaterialized()
        }
      } catch (e) {
        errorMessage.value = 'Failed to load locations.';
      } finally {
        loadingOptions.value = false;
      }
    }

    async function loadAvailableDates() {
      if (selectedLocationId.value == null) {
        availableDates.value = [];
        selectedDate.value = null;
        return;
      }

      loadingDates.value = true;
      try {
        const res = await http.get(`/api/v1/dashboards/materialized/${selectedLocationId.value}/dates`);
        const dates = res?.data?.dates || [];
        availableDates.value = dates;

        // Set default to most recent date (first in list)
        if (dates.length > 0) {
          selectedDate.value = dates[0];
        } else {
          selectedDate.value = null;
        }
      } catch (e) {
        console.error('Failed to load available dates', e);
        availableDates.value = [];
        selectedDate.value = null;
      } finally {
        loadingDates.value = false;
      }
    }

    async function loadMaterialized() {
      if (selectedLocationId.value == null) {
        materialized.value = null;
        return;
      }
      loadingData.value = true;
      errorMessage.value = '';
      noDataToday.value = false;
      try {
        // Load snapshot for selected date (or today if no date selected)
        const asOfDate = selectedDate.value || null;
        const url = asOfDate
          ? `/api/v1/dashboards/materialized/today?location_id=${selectedLocationId.value}&as_of_date=${asOfDate}`
          : `/api/v1/dashboards/materialized/today?location_id=${selectedLocationId.value}`;

        const res = await http.get(url);
        materialized.value = res?.data || null;

        // Track dashboard view
        if (materialized.value && selectedLocationId.value) {
          const parkName = currentOption.value?.label || materialized.value?.payload_json?.meta?.park_name || 'Unknown Park';
          activityTracker.logDashboardView(selectedLocationId.value, parkName);
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) {
          noDataToday.value = true;
          materialized.value = null;
        } else {
          errorMessage.value = 'Failed to load dashboard data.';
          materialized.value = null;
        }
      } finally {
        loadingData.value = false;
      }
    }

    const formatDateOption = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr + 'T00:00:00');
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      // Check if it's today or yesterday
      if (date.toDateString() === today.toDateString()) {
        return 'Today (' + dateStr + ')';
      }
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday (' + dateStr + ')';
      }

      // Otherwise return formatted date
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    onMounted(() => {
      loadOptions();
    });

    watch(selectedLocationId, async (n, o) => {
      if (n == null || n === o) return;
      await loadAvailableDates();
      // Force reload immediately after changing location
      // This ensures data loads even if both parks have the same snapshot date
      await loadMaterialized();
    });

    watch(selectedDate, async (n, o) => {
      // Don't skip if date is null
      if (n == null) return;
      // Only reload if date actually changed (location change handled above)
      if (n === o) return;
      await loadMaterialized();
    });

    return {
      // constants
      SECTION_IDS,
      // state
      loadingOptions,
      loadingData,
      loadingDates,
      errorMessage,
      options,
      groupedParks, // ADDED: Regional/seasonal park groupings
      noLocations,
      selectedLocationId,
      availableDates,
      selectedDate,
      materialized,
      noDataToday,
      currentOption,
      aiExpanded,
      // derived
      headerTitle,
      br,
      occ,
      arrivals,
      departures,
      call,
      socialReputation,
      callAccentClass,
      actionPlan,
      recommendations,
      feedbackUrl,
      arrivalGuests,
      departureGuests,
      guestFlowDetails,
      // engagement
      engagement,
      // methods
      prettyJson,
      currency,
      pctDisplay,
      pct,
      arrow,
      yoyClass,
      aiSection,
      toggleAISection,
      formatNumber,
      formatGeneratedTime,
      formatCompactDateTime,
      formatForecastDate,
      formatDateOption,
      getWeatherEmoji,
      weather,
    };
  },
};
</script>

<style scoped>
/* KPI Summary Bar - responsive to light/dark mode */
.kpi-summary-bar {
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 12px;
}

.kpi-summary-item {
  flex: 1;
  text-align: center;
  padding: 8px 4px;
}

.kpi-summary-label {
  font-size: 0.6875rem;
  color: var(--bs-secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 4px;
}

.kpi-summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--bs-body-color);
  line-height: 1;
  margin-bottom: 2px;
}

.kpi-summary-trend {
  font-size: 0.75rem;
  font-weight: 600;
}

.kpi-summary-meta {
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
  font-weight: 600;
}

.kpi-summary-status {
  font-size: 0.75rem;
  font-weight: 600;
}

.kpi-summary-status.status-good {
  color: #10b981;
}

.kpi-summary-status.status-warning {
  color: #ef4444;
}

.kpi-summary-divider {
  width: 1px;
  height: 40px;
  background-color: var(--bs-border-color);
  flex-shrink: 0;
}

/* Smooth scroll to anchored sections */
html {
  scroll-behavior: smooth;
}

[id^="revenue"], [id^="occupancy"], [id^="guests"], [id^="calls"], [id^="social"] {
  scroll-margin-top: 20px;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .kpi-summary-bar {
    flex-direction: column;
    gap: 16px;
  }

  .kpi-summary-divider {
    width: 100%;
    height: 1px;
  }

  .kpi-summary-item {
    width: 100%;
    padding: 8px 0;
  }
}

/* Existing styles */
.json-pre {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  margin: 0;
  max-height: 480px;
  overflow: auto;
}

.card-left-accent {
  border-left: 4px solid var(--bs-primary);
}

.text-purple {
  color: #7c3aed;
}

/* Feedback card styling */
.feedback-card {
  background-color: #eff6ff;
}

/* Dark mode support for feedback card */
:global(#app.dark-mode) .feedback-card {
  background-color: #0d1a2a;
  border-color: #0d6efd !important;
}

/* Extra small text for weather forecast */
.x-small {
  font-size: 0.7rem;
}

/* Location selector styling - Open/Closed visual distinction */
.location-select {
  font-size: 0.9rem;
}

/* Open parks - subtle green tint */
.location-select optgroup.optgroup-open {
  background-color: #f0f9f4;
  font-weight: 600;
  color: #047857;
}

.location-select option.option-open {
  background-color: #f0f9f4;
  color: #065f46;
  padding-left: 8px;
}

/* Closed parks - subtle gray tint */
.location-select optgroup.optgroup-closed {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #6b7280;
  font-style: italic;
}

.location-select option.option-closed {
  background-color: #f9fafb;
  color: #6b7280;
  font-style: italic;
  padding-left: 8px;
}

/* Divider option */
.location-select option.option-divider {
  background-color: #e5e7eb;
  color: #9ca3af;
  text-align: center;
  font-size: 0.75rem;
  padding: 2px 0;
}

/* Dark mode support for location selector */
:global(#app.dark-mode) .location-select optgroup.optgroup-open {
  background-color: #0a2e1f;
  color: #6ee7b7;
}

:global(#app.dark-mode) .location-select option.option-open {
  background-color: #0a2e1f;
  color: #a7f3d0;
}

:global(#app.dark-mode) .location-select optgroup.optgroup-closed {
  background-color: #16181d;
  color: #9ca3af;
}

:global(#app.dark-mode) .location-select option.option-closed {
  background-color: #0d0f12;
  color: #9ca3af;
}

:global(#app.dark-mode) .location-select option.option-divider {
  background-color: #1a1d23;
  color: #6b7280;
}
</style>
